/**
 * Contract Summary Utility Functions
 * TypeScript utilities for generating AI-powered contract summaries
 */
import { TRACKED_CHANGES_GUIDANCE } from './aiGuidance';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

export interface ContractSummarySection {
  title: string;
  content: string;
}

export interface ContractSummaryResult {
  title: string;
  parties: string[];
  summarySections: ContractSummarySection[];
  keyTerms: string[];
  metadata?: {
    contractDate?: string;
    expirationDate?: string;
    totalValue?: string;
  };
}

/**
 * Generates a comprehensive contract summary using OpenAI
 * @param documentText - The text content of the document to summarize
 * @param tenantId - Tenant ID for fetching AI keys from CLM
 * @returns Promise with contract summary results
 */
export async function generateContractSummary(documentText: string, tenantId: string): Promise<ContractSummaryResult> {
  try {
    // Truncate if too long (keep first 12000 chars for analysis)
    const textToAnalyze = documentText.length > 12000 
      ? documentText.substring(0, 12000) + '...' 
      : documentText;

    const prompt = `Analyze the following contract document and create a comprehensive summary. Extract and organize the information into clear sections.

For the summary, identify and create sections such as:
- Release and Discharge (if applicable)
- Consideration
- Scope of Settlement
- Binding Effect
- Governing Law
- Acknowledgment of Understanding
- Key Terms
- Obligations
- Important Clauses
- Any other relevant sections based on the document type

Also extract:
- Document title/type
- Parties involved (names or roles)
- Key terms and conditions
- Important dates, amounts, or values if mentioned

Return the response as a JSON object with this exact structure:
{
  "title": "Document title or type",
  "parties": ["Party 1", "Party 2"],
  "summarySections": [
    {
      "title": "Section Title",
      "content": "Detailed explanation of this section"
    }
  ],
  "keyTerms": [
    "Key term 1",
    "Key term 2"
  ],
  "metadata": {
    "contractDate": "date if found",
    "expirationDate": "date if found",
    "totalValue": "value if found"
  }
}

Contract text:
${textToAnalyze}

Return only valid JSON, no additional text.`;

    // Call backend API that fetches AI keys from tenant settings
    const response = await fetch(`${API_BASE_URL}/api/addin/ai-analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-addin-token': API_TOKEN,
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify({
        prompt,
        systemMessage:
          'You are a legal document analysis expert. Analyze contracts and create comprehensive, well-structured summaries. Always return valid JSON objects.' +
          TRACKED_CHANGES_GUIDANCE,
        temperature: 0.3,
        maxTokens: 3000,
        tenantId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`AI API error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const data = await response.json();
    const content = data.content || '{}';

    let summary: ContractSummaryResult;
    try {
      const cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
      summary = JSON.parse(jsonMatch ? jsonMatch[0] : cleanedContent);
    } catch (parseError) {
      console.error('Error parsing contract summary response:', parseError);
      console.error('Response content:', content);
      throw new Error('Failed to parse contract summary results');
    }

    // Validate and ensure proper structure
    if (!summary.title) summary.title = 'Contract Document';
    if (!Array.isArray(summary.parties)) summary.parties = [];
    if (!Array.isArray(summary.summarySections)) summary.summarySections = [];
    if (!Array.isArray(summary.keyTerms)) summary.keyTerms = [];

    return summary;
  } catch (error) {
    console.error('Error generating contract summary:', error);
    throw error;
  }
}

